import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import type { ListTableSort } from 'framework/ui/ui-builder';
import { clearCollectionViewParamsCache, readCollectionViewParamsCache, writeCollectionViewParamsCache } from './cache';
import { getDefaultCollectionViewParams } from './defaults';
import {
  areCollectionViewSearchesEqual,
  buildCollectionViewSearch,
  hasCollectionViewUrlParams,
  readCollectionViewUrlParams,
} from './url';
import type { CollectionViewParams, CollectionViewParamsConfig, CollectionViewParamsController } from './types';

interface LocationSync {
  pathname: string;
  search: string;
  syncingFromLocation: boolean;
}

const getInitialParams = <Filters>(pathname: string, search: string, config: CollectionViewParamsConfig<Filters>) =>
  config.syncWithUrl !== false && hasCollectionViewUrlParams(search, config)
    ? readCollectionViewUrlParams(search, config)
    : (readCollectionViewParamsCache(pathname, config) ?? getDefaultCollectionViewParams(config));

const useControlUrlParams = <Filters>(
  config: CollectionViewParamsConfig<Filters>,
): CollectionViewParamsController<Filters> => {
  const nextConfigWithDefaults = { ...config, cache: config.cache !== false };
  const configWithDefaultsRef = useRef(nextConfigWithDefaults);
  if (!isEqual(configWithDefaultsRef.current, nextConfigWithDefaults)) {
    configWithDefaultsRef.current = nextConfigWithDefaults;
  }
  const configWithDefaults = configWithDefaultsRef.current;
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useState(() => getInitialParams(location.pathname, location.search, configWithDefaults));
  const paramsSkippedFromCacheRef = useRef<CollectionViewParams<Filters> | null>(null);

  const locationSyncRef = useRef<LocationSync>({
    pathname: location.pathname,
    search: location.search,
    syncingFromLocation: false,
  });

  useEffect(() => {
    const sync = locationSyncRef.current;
    if (
      sync.pathname === location.pathname &&
      (configWithDefaults.syncWithUrl === false || areCollectionViewSearchesEqual(sync.search, location.search))
    ) {
      sync.search = location.search;
      return;
    }

    const pathnameChanged = sync.pathname !== location.pathname;
    const cachedParams = pathnameChanged ? readCollectionViewParamsCache(location.pathname, configWithDefaults) : null;
    const nextParams =
      configWithDefaults.syncWithUrl !== false && hasCollectionViewUrlParams(location.search, configWithDefaults)
        ? readCollectionViewUrlParams(location.search, configWithDefaults)
        : (cachedParams ?? getDefaultCollectionViewParams(configWithDefaults));

    locationSyncRef.current = {
      pathname: location.pathname,
      search: location.search,
      syncingFromLocation: !cachedParams,
    };
    setParams(nextParams);
  }, [configWithDefaults, location.pathname, location.search]);

  useEffect(() => {
    const sync = locationSyncRef.current;
    const writeCache = () => {
      if (paramsSkippedFromCacheRef.current === params) return;
      paramsSkippedFromCacheRef.current = null;
      writeCollectionViewParamsCache(sync.pathname, params, configWithDefaults);
    };

    if (configWithDefaults.syncWithUrl === false || sync.syncingFromLocation) {
      sync.syncingFromLocation = false;
      writeCache();
      return;
    }

    const nextSearch = buildCollectionViewSearch(sync.search, params, configWithDefaults);
    if (!areCollectionViewSearchesEqual(nextSearch, sync.search)) {
      sync.search = nextSearch;
      navigate({ search: nextSearch }, { replace: true });
    }

    writeCache();
  }, [configWithDefaults, navigate, params]);

  const setFilters = useCallback((nextFilters: SetStateAction<Filters>) => {
    setParams(current => ({
      ...current,
      filters:
        typeof nextFilters === 'function'
          ? (nextFilters as (filters: Filters) => Filters)(current.filters)
          : nextFilters,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    if (configWithDefaults.cache) clearCollectionViewParamsCache(location.pathname, configWithDefaults.cacheKey);
    setParams(current => {
      const nextParams = { ...current, filters: {} as Filters };
      paramsSkippedFromCacheRef.current = nextParams;
      return nextParams;
    });
  }, [configWithDefaults.cache, configWithDefaults.cacheKey, location.pathname]);

  const setSort = useCallback((sort: ListTableSort | null) => {
    setParams(current => ({ ...current, sort }));
  }, []);

  return useMemo(() => ({ params, setFilters, resetFilters, setSort }), [params, resetFilters, setFilters, setSort]);
};

export default useControlUrlParams;
